// IndexedDB database for customer management
import { openDB } from 'idb';

const DB_NAME = 'jimatpos_customer_db';
const STORE_NAME = 'customers';
const DB_VERSION = 1;

export type CustomerType = 'Walk-In' | 'Online' | 'Delivery';
export type LoyaltyTier = 'Regular' | 'Silver' | 'Gold' | 'Platinum';
export type PurchaseTime = 'Morning' | 'Afternoon' | 'Evening';
export type PurchaseChannel = 'POS' | 'Jimatshop';
export type CustomerTag = 'VIP' | 'Bulk Buyer' | 'Occasional Buyer' | 'New' | 'At Risk' | string;

export interface Customer {
  id: string;
  name: string;
  contactNo: string;
  email: string;
  customerType: CustomerType;
  loyaltyTier: LoyaltyTier;
  joinedDate: string;
  totalSpent: number;
  averageOrderValue: number;
  visitFrequency: number; // visits per month
  preferredPurchaseTime: PurchaseTime;
  preferredPurchaseChannel: PurchaseChannel;
  lastPurchaseDate: string;
  notes: string;
  tags: CustomerTag[];
  location?: string;
  loyaltyPoints: number;
}

export const initCustomerDb = async () => {
  console.log('Initializing customer database...');
  try {
    const db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Create a store of objects if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          console.log('Creating customer object store...');
          const store = db.createObjectStore(STORE_NAME, {
            keyPath: 'id',
            autoIncrement: false,
          });
          
          // Create indexes for searching and sorting
          store.createIndex('name', 'name', { unique: false });
          store.createIndex('email', 'email', { unique: true });
          store.createIndex('contactNo', 'contactNo', { unique: true });
          store.createIndex('customerType', 'customerType', { unique: false });
          store.createIndex('loyaltyTier', 'loyaltyTier', { unique: false });
          store.createIndex('lastPurchaseDate', 'lastPurchaseDate', { unique: false });
          store.createIndex('totalSpent', 'totalSpent', { unique: false });
        } else {
          console.log('Customer object store already exists');
        }
      },
    });
    console.log('Customer database initialized successfully');
    return db;
  } catch (error) {
    console.error('Error initializing customer database:', error);
    throw error;
  }
};

// Add mock data for development
export const addMockCustomers = async () => {
  console.log('Adding mock customers...');
  try {
    const db = await initCustomerDb();
    
    // Check if data already exists
    const count = await db.count(STORE_NAME);
    console.log(`Current customer count: ${count}`);
    
    if (count === 0) {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      
      const mockCustomers: Customer[] = [
        {
          id: '1',
          name: 'Ahmad Rosli',
          contactNo: '0123456789',
          email: 'ahmad@example.com',
          customerType: 'Walk-In',
          loyaltyTier: 'Gold',
          joinedDate: '2023-01-15',
          totalSpent: 3450.50,
          averageOrderValue: 115.02,
          visitFrequency: 8,
          preferredPurchaseTime: 'Morning',
          preferredPurchaseChannel: 'POS',
          lastPurchaseDate: '2025-05-10',
          notes: 'Prefers fresh produce',
          tags: ['VIP', 'Bulk Buyer'],
          location: 'Kuala Lumpur',
          loyaltyPoints: 450
        },
        {
          id: '2',
          name: 'Lisa Wong',
          contactNo: '0198765432',
          email: 'lisa@example.com',
          customerType: 'Online',
          loyaltyTier: 'Silver',
          joinedDate: '2023-04-22',
          totalSpent: 1250.75,
          averageOrderValue: 78.17,
          visitFrequency: 4,
          preferredPurchaseTime: 'Evening',
          preferredPurchaseChannel: 'Jimatshop',
          lastPurchaseDate: '2025-05-05',
          notes: 'Buys mostly household items',
          tags: ['Occasional Buyer'],
          location: 'Petaling Jaya',
          loyaltyPoints: 220
        },
        {
          id: '3',
          name: 'Suresh Nair',
          contactNo: '0167894563',
          email: 'suresh@example.com',
          customerType: 'Delivery',
          loyaltyTier: 'Platinum',
          joinedDate: '2022-11-05',
          totalSpent: 7890.25,
          averageOrderValue: 197.26,
          visitFrequency: 10,
          preferredPurchaseTime: 'Afternoon',
          preferredPurchaseChannel: 'POS',
          lastPurchaseDate: '2025-05-15',
          notes: 'Bulk orders for office supplies',
          tags: ['VIP', 'Bulk Buyer'],
          location: 'Shah Alam',
          loyaltyPoints: 890
        },
        {
          id: '4',
          name: 'Mei Ling Tan',
          contactNo: '0187654321',
          email: 'meiling@example.com',
          customerType: 'Walk-In',
          loyaltyTier: 'Regular',
          joinedDate: '2023-08-12',
          totalSpent: 550.30,
          averageOrderValue: 45.86,
          visitFrequency: 3,
          preferredPurchaseTime: 'Morning',
          preferredPurchaseChannel: 'POS',
          lastPurchaseDate: '2025-04-28',
          notes: 'Usually buys for weekend cooking',
          tags: ['New'],
          location: 'Cheras',
          loyaltyPoints: 75
        },
        {
          id: '5',
          name: 'Raj Kumar',
          contactNo: '0134567890',
          email: 'raj@example.com',
          customerType: 'Online',
          loyaltyTier: 'Gold',
          joinedDate: '2022-12-20',
          totalSpent: 4320.15,
          averageOrderValue: 144.01,
          visitFrequency: 7,
          preferredPurchaseTime: 'Evening',
          preferredPurchaseChannel: 'Jimatshop',
          lastPurchaseDate: '2025-05-12',
          notes: 'Frequently buys premium products',
          tags: ['VIP'],
          location: 'Subang Jaya',
          loyaltyPoints: 520
        },
        {
          id: '6',
          name: 'Nurul Izzah',
          contactNo: '0192345678',
          email: 'nurul@example.com',
          customerType: 'Walk-In',
          loyaltyTier: 'Silver',
          joinedDate: '2023-02-28',
          totalSpent: 1800.45,
          averageOrderValue: 90.02,
          visitFrequency: 5,
          preferredPurchaseTime: 'Morning',
          preferredPurchaseChannel: 'POS',
          lastPurchaseDate: '2025-05-08',
          notes: 'Buys organic products primarily',
          tags: ['Health Conscious'],
          location: 'Ampang',
          loyaltyPoints: 210
        },
        {
          id: '7',
          name: 'David Chen',
          contactNo: '0123987654',
          email: 'david@example.com',
          customerType: 'Delivery',
          loyaltyTier: 'Platinum',
          joinedDate: '2022-10-15',
          totalSpent: 9500.75,
          averageOrderValue: 220.50,
          visitFrequency: 12,
          preferredPurchaseTime: 'Afternoon',
          preferredPurchaseChannel: 'Jimatshop',
          lastPurchaseDate: '2025-05-14',
          notes: 'Orders in bulk for restaurant',
          tags: ['VIP', 'Bulk Buyer', 'Business'],
          location: 'Puchong',
          loyaltyPoints: 1200
        }
      ];
      
      try {
        // Add mock customers
        for (const customer of mockCustomers) {
          await tx.store.add(customer);
          console.log(`Added mock customer: ${customer.name}`);
        }
        
        await tx.done;
        console.log('Added all mock customer data successfully');
      } catch (error) {
        console.error('Error adding mock customers:', error);
        tx.abort();
        throw error;
      }
    } else {
      console.log('Customer data already exists, skipping mock data addition');
    }
  } catch (error) {
    console.error('Error in addMockCustomers:', error);
    throw error;
  }
};

// Customer data operations
export const getAllCustomers = async (): Promise<Customer[]> => {
  console.log('Fetching all customers...');
  try {
    const db = await initCustomerDb();
    const customers = await db.getAll(STORE_NAME);
    console.log(`Retrieved ${customers.length} customers`);
    return customers;
  } catch (error) {
    console.error('Error getting all customers:', error);
    return [];
  }
};

export const getCustomerById = async (id: string): Promise<Customer | undefined> => {
  const db = await initCustomerDb();
  return db.get(STORE_NAME, id);
};

export const addCustomer = async (customer: Omit<Customer, 'id'>): Promise<string> => {
  const db = await initCustomerDb();
  const id = `customer_${Date.now()}`;
  await db.add(STORE_NAME, { ...customer, id });
  return id;
};

export const updateCustomer = async (customer: Customer): Promise<void> => {
  const db = await initCustomerDb();
  await db.put(STORE_NAME, customer);
};

export const deleteCustomer = async (id: string): Promise<void> => {
  const db = await initCustomerDb();
  await db.delete(STORE_NAME, id);
};

// Segmentation utilities
export const getCustomersBySpendingTier = async (): Promise<Record<string, Customer[]>> => {
  console.log('Getting customers by spending tier...');
  try {
    const customers = await getAllCustomers();
    const result = {
      'High Spenders': customers.filter(c => c.totalSpent > 3000),
      'Medium Spenders': customers.filter(c => c.totalSpent >= 1000 && c.totalSpent <= 3000),
      'Low Spenders': customers.filter(c => c.totalSpent < 1000),
    };
    console.log(`Segmented customers by spending tier: High=${result['High Spenders'].length}, Medium=${result['Medium Spenders'].length}, Low=${result['Low Spenders'].length}`);
    return result;
  } catch (error) {
    console.error('Error getting customers by spending tier:', error);
    return { 'High Spenders': [], 'Medium Spenders': [], 'Low Spenders': [] };
  }
};

export const getCustomersByFrequency = async (): Promise<Record<string, Customer[]>> => {
  console.log('Getting customers by frequency...');
  try {
    const customers = await getAllCustomers();
    const result = {
      'Frequent': customers.filter(c => c.visitFrequency > 6),
      'Regular': customers.filter(c => c.visitFrequency >= 3 && c.visitFrequency <= 6),
      'Occasional': customers.filter(c => c.visitFrequency < 3),
    };
    console.log(`Segmented customers by frequency: Frequent=${result['Frequent'].length}, Regular=${result['Regular'].length}, Occasional=${result['Occasional'].length}`);
    return result;
  } catch (error) {
    console.error('Error getting customers by frequency:', error);
    return { 'Frequent': [], 'Regular': [], 'Occasional': [] };
  }
};

export const getCustomersByLoyaltyTier = async (): Promise<Record<LoyaltyTier, Customer[]>> => {
  console.log('Getting customers by loyalty tier...');
  try {
    const customers = await getAllCustomers();
    const result: Partial<Record<LoyaltyTier, Customer[]>> = {};
    
    customers.forEach(customer => {
      if (!result[customer.loyaltyTier]) {
        result[customer.loyaltyTier] = [];
      }
      result[customer.loyaltyTier]?.push(customer);
    });
    
    console.log(`Segmented customers by loyalty tier: ${Object.keys(result).join(', ')}`);
    return result as Record<LoyaltyTier, Customer[]>;
  } catch (error) {
    console.error('Error getting customers by loyalty tier:', error);
    return { 'Regular': [], 'Silver': [], 'Gold': [], 'Platinum': [] };
  }
};

// Initialize the database and add mock data
export const initializeCustomerDatabase = async () => {
  console.log('Initializing complete customer database...');
  try {
    await initCustomerDb();
    await addMockCustomers();
    console.log('Customer database fully initialized');
    
    // Verify data was added
    const customers = await getAllCustomers();
    console.log(`Verified ${customers.length} customers in database`);
    
    return customers;
  } catch (error) {
    console.error('Failed to initialize customer database completely:', error);
    throw error;
  }
};
